/**
 * Get answer value for a specific question in a plan year
 * @param {Object} planYear - The plan year object with answers array
 * @param {string} questionId - The question ID to search for
 * @returns {string|null} The answer value or null if not found
 */
export const getAnswerForQuestion = (planYear, questionId) => {
  if (!planYear.answers || !Array.isArray(planYear.answers)) return null
  
  const answer = planYear.answers.find(a => a.question_id === questionId)
  return answer ? answer.answer : null
}

/**
 * Parse answer string to array (handles formats like "[1,2,3]" or "1")
 * @param {string} answerStr - The answer string
 * @returns {Array} Array of values
 */
export const parseAnswerToArray = (answerStr) => {
  if (!answerStr) return []
  
  try {
    // If it's already in array format like "[1,2,3]"
    if (answerStr.startsWith('[') && answerStr.endsWith(']')) {
      return JSON.parse(answerStr)
    }
    // If it's a single value like "1"
    return [parseInt(answerStr)]
  } catch (error) {
    console.error('Error parsing answer:', answerStr, error)
    return []
  }
}

/**
 * Normalizes an answer value from string to appropriate type.
 * @param {*} answer - The raw answer value
 * @returns {*} - Normalized answer (number, array, or original value)
 */
export function normalizeAnswer(answer) {
  if (answer && answer.length > 0) {
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(answer)) {
      return answer
    }
    if (isNaN(answer)) {
      let returnValue = null
      try {
        returnValue = JSON.parse(answer)
      } catch (error) {
        returnValue = answer
      }
      return returnValue
    } else {
      return parseInt(answer, 10)
    }
  }
  return answer
}

/**
 * Normalizes an answer for score-calculation contexts, applying the type-4
 * (ERISA date) transformation that QuestionsStore performs: when a type-4
 * question has a date answer (not '1', '2', or empty), convert it to 0.
 * Without this, calculateSectionScore sees the date string, fails the
 * The !isNaN() check, and excludes the question from score contribution.
 * @param {*} rawAnswer - The raw answer value (from Firestore or user input)
 * @param {Object} question - The question object (must include a `type` field)
 * @returns {*} - Normalized answer safe for score calculations
 */
export function normalizeAnswerForScore(rawAnswer, question) {
  let value = (rawAnswer !== undefined && rawAnswer !== null) ? normalizeAnswer(rawAnswer) : null

  if (question?.type === 4 && value !== null && value !== undefined) {
    const strVal = String(value)
    if (strVal !== '1' && strVal !== '2' && strVal !== '') {
      if (isNaN(value)) {
        value = 0
      }
    }
  }

  return value
}

/**
 * Builds an answers map from an array of answer objects.
 * @param {Array} answers - Array of answer objects with question_id and answer properties
 * @returns {Object} - Map of questionId -> normalized answer value
 */
export function buildAnswersMap(answers) {
  if (!answers || !Array.isArray(answers)) return {}
  return answers.reduce((map, ans) => {
    const questionId = ans.question_id || ans.questionId
    if (questionId) {
      map[questionId] = normalizeAnswer(ans.answer)
    }
    return map
  }, {})
}

/**
 * Core function to determine if a question should be shown based on hideLogic.
 * This is the centralized logic used by both QuestionsStore and ScoreCalculationsService.
 * 
 * @param {Object} question - The question object with hideLogic and hideReason
 * @param {Function} getAnswer - Function that takes questionId and returns the answer value
 * @returns {Object} - { show: boolean, hideReason: string, questionHideReason: any, reasons: array, questionsHideReasons: array }
 */
export function showQuestionCore(question, getAnswer) {
  let hideLogic = question.hideLogic
  let show = true
  
  if (hideLogic) {
    const hideLogicObject = typeof hideLogic === 'string' ? JSON.parse(hideLogic) : hideLogic
    const results = []
    
    hideLogicObject.values.forEach((val) => {
      let questionId = val.length === 3 
        ? (typeof val[0] === 'string' ? val[1] : val[0])
        : val[0]
      let value = val.length === 3 ? val[2] : val[1]
      let currentQuestionState = getAnswer(questionId)
      
      if (currentQuestionState !== null && currentQuestionState !== undefined && currentQuestionState !== '') {
        if (val.length === 3) {
          if (typeof val[0] === 'string') {
            if (currentQuestionState instanceof Array) {
              if (val[0] === 'not') {
                results.push(!currentQuestionState.includes(Math.abs(value)))
              } else {
                results.push(
                  value < 0 
                    ? !currentQuestionState.includes(Math.abs(value))
                    : currentQuestionState.includes(value)
                )
              }
            } else {
              results.push([
                val[0],
                val[0] === 'not' ? currentQuestionState != value : currentQuestionState == value
              ])
            }
          } else {
            let operator = val[1]
            switch (operator) {
              case '<=':
                results.push(currentQuestionState <= value)
                break
              case '>=':
                results.push(currentQuestionState >= value)
                break
              case '==':
                results.push(currentQuestionState === value)
                break
              case '>':
                results.push(currentQuestionState > value)
                break
              case '<':
                results.push(currentQuestionState < value)
                break
            }
          }
        } else {
          if (currentQuestionState instanceof Array) {
            results.push(
              value < 0 
                ? !currentQuestionState.includes(Math.abs(value))
                : currentQuestionState.includes(value)
            )
          } else {
            results.push(currentQuestionState === value)
          }
        }
      } else {
        if (val.length === 3) {
          if (typeof val[0] === 'string') {
            results.push([val[0], false])
          } else {
            results.push(false)
          }
        } else {
          results.push(false)
        }
      }
    })

    if (results && results.length > 0) {
      let triggerIndex = 0
      const triggerIndexes = []
      show = results.reduce((acc, val, index) => {
        let localReturn = false
        const previousValue = acc instanceof Array ? acc[1] : acc
        if (val instanceof Array) {
          localReturn = val[0] === 'and' ? previousValue && val[1] : previousValue || val[1]
          if (localReturn && val[1]) {
            triggerIndex = index
            triggerIndexes.push(index)
          }
        } else {
          localReturn = previousValue && val
          if (localReturn) {
            triggerIndexes.push(index)
          }
        }
        return localReturn
      })
      
      if (show && triggerIndexes.length === 0) {
        triggerIndexes.push(0)
      }
      
      let hideReasonsJson = question.hideReason !== null && show && question.hideReason !== undefined
        ? question.hideReason
        : { value: [] }
      
      let reason = hideReasonsJson.value.length > 0 && show
        ? (triggerIndex >= hideReasonsJson.value.length 
            ? hideReasonsJson.value[0] 
            : hideReasonsJson.value[triggerIndex])
        : ''
      
      const reasons = hideReasonsJson.value.length > 0 && show
        ? [...new Set(triggerIndexes.map((idx) => 
            idx >= hideReasonsJson.value.length ? hideReasonsJson.value[0] : hideReasonsJson.value[idx]
          ))]
        : []
      
      let questionHideReason = Number.isInteger(hideLogicObject.values[triggerIndex][0])
        ? hideLogicObject.values[triggerIndex][0]
        : hideLogicObject.values[triggerIndex][1]

      const questionsHideReasons = [...new Set(triggerIndexes.map(
        (trgIndex) => Number.isInteger(hideLogicObject.values[trgIndex][0])
          ? hideLogicObject.values[trgIndex][0]
          : hideLogicObject.values[trgIndex][1]
      ))]

      return { show: !show, hideReason: reason, questionHideReason, reasons, questionsHideReasons }
    } else {
      return { show, hideReason: '' }
    }
  } else {
    return { show, hideReason: '' }
  }
}

/**
 * Checks if a section should be hidden due to ICHRA.
 * @param {string} key - The section key (e.g., 'ICHRA')
 * @param {Function} getAnswer - Function that takes questionId and returns the answer value
 * @returns {boolean} - True if section should be hidden
 */
export function hideDueICHRACore(key, getAnswer) {
  if (key === 'ICHRA') {
    let initialOptions = getAnswer(12)
    if (typeof initialOptions === 'object' && initialOptions?.value) {
      initialOptions = JSON.parse(initialOptions.value)
    }
    return !initialOptions?.includes(10)
  }
  return false
}

/**
 * Checks if all questions in an array have answers.
 * @param {Array} questions - Array of question objects
 * @returns {boolean} - True if all questions are complete
 */
export function isQuestionsComplete(questions) {
  let pendingQuestions = questions.filter((val) => {
    if (val.answer !== undefined) {
      if (typeof val.answer === 'object') {
        return Object.keys(val.answer).length === 0
      }
      return val.answer === '' || val.answer === null || val.answer === undefined
    } else {
      return true
    }
  })
  return pendingQuestions.length === 0
}

/**
 * Checks if assessment is complete.
 * @param {Object} assessmentQuestions - Assessment questions grouped by section
 * @param {Function} getAnswer - Function that takes questionId and returns the answer value
 * @returns {boolean} - True if assessment is complete
 */
export function isAssessmentCompleteCore(assessmentQuestions, getAnswer) {
  if (!assessmentQuestions || Object.keys(assessmentQuestions).length === 0) {
    return false
  }
  let finalAssessmentQuestions = []
  Object.values(assessmentQuestions).forEach((section) => {
    section.forEach((question) => {
      if (showQuestionCore(question, getAnswer).show) {
        finalAssessmentQuestions.push(question)
      }
    })
  })
  return isQuestionsComplete(finalAssessmentQuestions)
}

/**
 * Checks if plan is complete.
 * @param {Object} planQuestions - Plan questions grouped by section
 * @returns {boolean} - True if plan is complete
 */
export function isPlanCompleteCore(planQuestions) {
  if (!planQuestions || Object.keys(planQuestions).length === 0) {
    return false
  }
  const section = Object.values(planQuestions)[0] ?? []
  if (section.length === 0) {
    return false
  }
  const requiredPlanQuestions = section.filter((question) => !question.isOptional)
  return isQuestionsComplete(requiredPlanQuestions)
}

export function findPlanQuestionById(sectionQuestions, questionId) {
  if (!Array.isArray(sectionQuestions)) return undefined
  return sectionQuestions.find((q) => String(q.id) === String(questionId))
}

function planOptionLabel(question) {
  if (!question || question.answer === undefined || question.answer === null || question.answer === '') {
    return ''
  }
  return question.options?.[question.answer] ?? ''
}

/**
 * PDF/report fields from plan questions by catalog id (not array index).
 * newHire stays empty: question 10 was removed; Carbone templates may still read the field.
 */
export function derivePlanPdfFields(sectionQuestions) {
  const q = (id) => findPlanQuestionById(sectionQuestions, id)
  const coveragesQ = q('12')
  return {
    controlledGroup: planOptionLabel(q('7')),
    erisaPlan: planOptionLabel(q('8')),
    newHire: '',
    medicalPlan: planOptionLabel(q('11')),
    fullTimeEmployers: q('9')?.answer ?? '',
    coverages: Array.isArray(coveragesQ?.answer)
      ? coveragesQ.answer.map((ans) => coveragesQ.options?.[ans]).filter(Boolean)
      : [],
  }
}

/**
 * Conditional options: `conditional_options = { "<optionIndex>": { show_when: [[questionId, value], ...] } }`
 * (stored in Firestore as a JSON string, like hideLogic; objects are accepted too).
 * The option is offered only while every show_when pair matches (AND); an unanswered controlling
 * question hides it. Conditional options are appended last so stored answer indexes never shift.
 * ACA: questions/24 → { "4": { show_when: [[23, 3]] } } offers "N/A" on Q3 while Q2 is "Not currently...".
 */
export function parseConditionalOptions(question) {
  const raw = question?.conditional_options ?? question?.conditionalOptions
  if (!raw) return {}
  let parsed = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch (error) {
      return {}
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  return parsed
}

function conditionMatches(answer, expected) {
  if (answer === null || answer === undefined || answer === '') return false
  if (Array.isArray(answer)) return answer.includes(expected)
  if (typeof answer === 'number') return answer === Number(expected)
  if (typeof answer === 'string' && answer.trim() !== '' && !isNaN(answer)) {
    return Number(answer) === Number(expected)
  }
  return answer === expected
}

/** Whether optionIndex can be offered; options without a rule are always visible. */
export function isOptionVisible(question, optionIndex, getAnswer) {
  const rule = parseConditionalOptions(question)[String(optionIndex)]
  if (!rule) return true
  const showWhen = Array.isArray(rule.show_when) ? rule.show_when : rule.showWhen
  if (!Array.isArray(showWhen) || showWhen.length === 0) return true
  return showWhen.every(
    (pair) => Array.isArray(pair) && pair.length >= 2 && conditionMatches(getAnswer(pair[0]), pair[1])
  )
}

/** Option labels regardless of the stored shape: array, { values } or JSON string. */
export function getOptionLabels(question) {
  let options = question?.options
  if (typeof options === 'string') {
    try {
      options = JSON.parse(options)
    } catch (error) {
      return []
    }
  }
  if (Array.isArray(options)) return options
  if (options && Array.isArray(options.values)) return options.values
  return []
}

/** Options to render, keeping the ORIGINAL index so v-model, element ids and saved answers stay aligned. */
export function getVisibleOptions(question, getAnswer) {
  return getOptionLabels(question)
    .map((label, index) => ({ index, label }))
    .filter(({ index }) => isOptionVisible(question, index, getAnswer))
}

/** Questions whose current answer is a conditional option that is no longer visible (to be cleared). */
export function findOrphanConditionalAnswers(questions, getAnswer) {
  const list = Array.isArray(questions) ? questions : Object.values(questions || {}).flat()
  const orphans = []
  list.forEach((question) => {
    const conditionalIndexes = Object.keys(parseConditionalOptions(question)).map(Number)
    if (conditionalIndexes.length === 0) return
    const isOrphan = (idx) => conditionalIndexes.includes(idx) && !isOptionVisible(question, idx, getAnswer)
    const answer = getAnswer(question.id)
    if (typeof answer === 'number') {
      if (isOrphan(answer)) orphans.push({ question, optionIndexes: [answer] })
    } else if (Array.isArray(answer)) {
      const bad = answer.filter((idx) => typeof idx === 'number' && isOrphan(idx))
      if (bad.length > 0) orphans.push({ question, optionIndexes: bad })
    }
  })
  return orphans
}
